import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type CreateAppInput } from './api.js';

/** Creates a catalogue entry by hand. */
export function NewAppForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('utilities');
  const [summaryEn, setSummaryEn] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [summaryRu, setSummaryRu] = useState('');
  const [descriptionRu, setDescriptionRu] = useState('');
  const [homepage, setHomepage] = useState('');
  const [license, setLicense] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const input: CreateAppInput = {
        id: id.trim(),
        name: name.trim(),
        category,
        summary: { en: summaryEn.trim(), ...(summaryRu.trim() ? { ru: summaryRu.trim() } : {}) },
        description: {
          en: descriptionEn.trim(),
          ...(descriptionRu.trim() ? { ru: descriptionRu.trim() } : {}),
        },
        ...(homepage.trim() ? { homepageUrl: homepage.trim() } : {}),
        ...(license.trim() ? { license: license.trim() } : {}),
      };
      await api.createApp(input);
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось создать приложение');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    /^[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9.-]*$/.test(id.trim()) &&
    name.trim().length > 0 &&
    summaryEn.trim().length > 0 &&
    descriptionEn.trim().length > 0;

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Новое приложение</h2>

        <div className="mt-4 space-y-3">
          <Field label="Идентификатор" hint="Формат: издатель/приложение. Изменить потом нельзя.">
            <input
              value={id}
              onChange={(event) => setId(event.target.value)}
              placeholder="vendor/app-name"
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>
          <Field label="Название">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>
          <Field label="Категория">
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            >
              {[
                'audio-video',
                'business',
                'communication',
                'development',
                'education',
                'games',
                'graphics',
                'health',
                'news',
                'productivity',
                'security',
                'system',
                'utilities',
              ].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Краткое описание (английский, обязательно)">
            <input
              value={summaryEn}
              onChange={(event) => setSummaryEn(event.target.value)}
              maxLength={200}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>
          <Field label="Краткое описание (русский)">
            <input
              value={summaryRu}
              onChange={(event) => setSummaryRu(event.target.value)}
              maxLength={200}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>
          <Field label="Полное описание (английский, обязательно)">
            <textarea
              value={descriptionEn}
              onChange={(event) => setDescriptionEn(event.target.value)}
              rows={3}
              disabled={busy}
              className="w-full rounded-lg border border-border bg-bg p-3 text-sm"
            />
          </Field>
          <Field label="Полное описание (русский)">
            <textarea
              value={descriptionRu}
              onChange={(event) => setDescriptionRu(event.target.value)}
              rows={3}
              disabled={busy}
              className="w-full rounded-lg border border-border bg-bg p-3 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Сайт">
              <input
                value={homepage}
                onChange={(event) => setHomepage(event.target.value)}
                placeholder="https://"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Лицензия (SPDX)">
              <input
                value={license}
                onChange={(event) => setLicense(event.target.value)}
                placeholder="MIT"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
          </div>
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy || !valid}>
            {busy ? 'Создание…' : 'Создать'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
